import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Switch,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/Modal';
import Button from 'components/ui/Button';
import SimpleSlider from 'components/ui/Slider';
import {
  EVALUATION_TYPE_CONFIGS,
  TemplateField,
  TIPO_EVALUACION_META,
} from 'constants/evaluationTemplates';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { useCreateEvaluation, useUpdateEvaluation } from 'hooks/useEvaluations';
import React, { useEffect, useState } from 'react';
import { MdAdd, MdClose, MdFitnessCenter, MdScience, MdStraighten } from 'react-icons/md';
import { ClinicalEpisode, PhysicalEvaluation, TipoEvaluacion } from 'types/models';

const TODAY = new Date().toISOString().split('T')[0];

const EVA_EMOJIS = ['😊', '🙂', '🙂', '😐', '😐', '😕', '😕', '😢', '😢', '😭', '😭'];

interface KVRow    { key: string; value: string; }
interface PruebaRow { nombre: string; resultado: 'positivo' | 'negativo' | 'dudoso'; notas: string; }

function recordToRows(rec: Record<string, number> | null): KVRow[] {
  if (!rec || Object.keys(rec).length === 0) return [{ key: '', value: '' }];
  return Object.entries(rec).map(([key, value]) => ({ key, value: String(value) }));
}

function pruebasToRows(rec: Record<string, { resultado: string; notas?: string }> | null): PruebaRow[] {
  if (!rec || Object.keys(rec).length === 0) return [];
  return Object.entries(rec).map(([nombre, p]) => ({
    nombre,
    resultado: p.resultado as PruebaRow['resultado'],
    notas: p.notas ?? '',
  }));
}

// ── Sub-components defined OUTSIDE main component to prevent remount on render ──

interface SectionLabelProps { icon: any; title: string; color: string; }
function SectionLabel({ icon, title, color }: SectionLabelProps) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  return (
    <Flex align='center' gap='8px'>
      <Flex w='24px' h='24px' borderRadius='6px' bg={color} align='center' justify='center' flexShrink={0}>
        <Icon as={icon} color='white' w='14px' h='14px' />
      </Flex>
      <Text fontWeight='700' color={textColor} fontSize='sm'>{title}</Text>
    </Flex>
  );
}

interface TAProps {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; rows?: number; maxLen?: number;
}
function TA({ label, value, onChange, placeholder, rows = 2, maxLen }: TAProps) {
  const mutedColor  = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const inputBg     = useColorModeValue('gray.50', 'navy.700');
  const inputBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  return (
    <FormControl mb='14px'>
      <FormLabel ms='2px' fontSize='xs' fontWeight='700' color={mutedColor} mb='4px'>{label}</FormLabel>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        bg={inputBg}
        border='1px solid'
        borderColor={inputBorder}
        borderRadius='12px'
        fontSize='sm'
        resize='vertical'
        maxLength={maxLen}
        _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
      />
    </FormControl>
  );
}

interface KVSectionProps {
  label: string; unit: string; rows: KVRow[];
  setRows: React.Dispatch<React.SetStateAction<KVRow[]>>;
  prefix: string; valuePlaceholder: string; valueMax?: number;
  errors: Record<string, string>;
}
function KVSection({ label, unit, rows, setRows, prefix, valuePlaceholder, valueMax, errors }: KVSectionProps) {
  const mutedColor  = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const rowBg       = useColorModeValue('gray.50', 'navy.750');
  const rowBorder   = useColorModeValue('gray.200', 'whiteAlpha.150');

  const updateKV = (idx: number, field: 'key' | 'value', val: string) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));

  const addRow = () => setRows((prev) => [...prev, { key: '', value: '' }]);

  const removeRow = (idx: number) => setRows((prev) => {
    if (prev.length === 1) return [{ key: '', value: '' }];
    return prev.filter((_, i) => i !== idx);
  });

  return (
    <Box mb='14px'>
      <Text fontSize='xs' fontWeight='700' color={mutedColor} mb='8px'>{label}</Text>
      <Flex direction='column' gap='6px'>
        {rows.map((row, i) => (
          <Flex key={i} gap='8px' align='flex-start'>
            <Input
              value={row.key}
              onChange={(e) => updateKV(i, 'key', e.target.value)}
              placeholder='Ej: hombroFlexionD'
              bg={rowBg}
              border='1px solid'
              borderColor={rowBorder}
              borderRadius='10px'
              fontSize='sm'
              flex={2}
              _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
            />
            <FormControl isInvalid={!!errors[`${prefix}_${i}`]} flex={1}>
              <Input
                value={row.value}
                onChange={(e) => updateKV(i, 'value', e.target.value)}
                placeholder={valuePlaceholder}
                type='number'
                max={valueMax}
                min={0}
                bg={rowBg}
                border='1px solid'
                borderColor={errors[`${prefix}_${i}`] ? 'red.400' : rowBorder}
                borderRadius='10px'
                fontSize='sm'
                _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
              />
              {errors[`${prefix}_${i}`] && (
                <FormErrorMessage fontSize='10px' ms='4px'>{errors[`${prefix}_${i}`]}</FormErrorMessage>
              )}
            </FormControl>
            <Text fontSize='xs' color={mutedColor} mt='10px' flexShrink={0}>{unit}</Text>
            <IconButton
              aria-label='Eliminar'
              icon={<Icon as={MdClose} />}
              size='sm'
              variant='ghost'
              colorScheme='gray'
              onClick={() => removeRow(i)}
            />
          </Flex>
        ))}
        <Button size='xs' variant='ghost' leftIcon={<Icon as={MdAdd} />} onClick={addRow} alignSelf='flex-start'>
          Añadir {label.toLowerCase()}
        </Button>
      </Flex>
    </Box>
  );
}

// ── Type selector cards ─────────────────────────────────────────────────────────

interface TypeCardProps {
  tipo: TipoEvaluacion;
  selected: boolean;
  onSelect: () => void;
}

function TypeCard({ tipo, selected, onSelect }: TypeCardProps) {
  const meta       = TIPO_EVALUACION_META[tipo];
  const bg         = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.150');
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  return (
    <Box
      as='button'
      type='button'
      onClick={onSelect}
      bg={selected ? `${meta.color}.50` : bg}
      border='2px solid'
      borderColor={selected ? `${meta.color}.400` : borderColor}
      borderRadius='12px'
      px='14px'
      py='12px'
      textAlign='left'
      cursor='pointer'
      transition='all 0.15s'
      _hover={{ borderColor: `${meta.color}.300`, bg: `${meta.color}.50` }}
      w='100%'
    >
      <Text fontSize='sm' fontWeight='700' color={selected ? `${meta.color}.700` : textColor} mb='2px'>
        {meta.label}
      </Text>
      <Text fontSize='xs' color={selected ? `${meta.color}.500` : mutedColor} lineHeight='1.4'>
        {meta.description}
      </Text>
    </Box>
  );
}

// ── Dynamic template field renderer ──────────────────────────────────────────────

interface TemplateFieldInputProps {
  field: TemplateField;
  value: unknown;
  onChange: (v: unknown) => void;
}

function TemplateFieldInput({ field, value, onChange }: TemplateFieldInputProps) {
  const inputBg     = useColorModeValue('gray.50', 'navy.700');
  const inputBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedColor  = useColorModeValue('secondaryGray.500', 'secondaryGray.400');

  if (field.type === 'select') {
    return (
      <Select
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        bg={inputBg}
        border='1px solid'
        borderColor={inputBorder}
        borderRadius='10px'
        fontSize='sm'
        placeholder='Seleccionar...'
        _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
      >
        {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
      </Select>
    );
  }

  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (opt: string) => {
      if (selected.includes(opt)) onChange(selected.filter((s) => s !== opt));
      else onChange([...selected, opt]);
    };
    return (
      <Flex flexWrap='wrap' gap='6px'>
        {field.options?.map((o) => (
          <Badge
            key={o}
            as='button'
            type='button'
            onClick={() => toggle(o)}
            colorScheme={selected.includes(o) ? 'brand' : 'gray'}
            variant={selected.includes(o) ? 'solid' : 'outline'}
            borderRadius='full'
            px='10px'
            py='4px'
            fontSize='xs'
            cursor='pointer'
            fontWeight='600'
            transition='all 0.12s'
          >
            {o}
          </Badge>
        ))}
      </Flex>
    );
  }

  if (field.type === 'boolean') {
    return (
      <Flex align='center' gap='10px'>
        <Switch
          isChecked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          colorScheme='brand'
        />
        <Text fontSize='sm' color={mutedColor}>{Boolean(value) ? 'Sí' : 'No'}</Text>
      </Flex>
    );
  }

  if (field.type === 'score' || field.type === 'number') {
    return (
      <Flex align='center' gap='8px'>
        <NumberInput
          value={value !== undefined && value !== null ? Number(value) : ''}
          onChange={(_, n) => onChange(isNaN(n) ? '' : n)}
          min={field.min}
          max={field.max}
          maxW='120px'
        >
          <NumberInputField
            bg={inputBg}
            border='1px solid'
            borderColor={inputBorder}
            borderRadius='10px'
            fontSize='sm'
            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
            placeholder={String(field.min ?? 0)}
          />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        {field.unit && (
          <Text fontSize='xs' color={mutedColor}>{field.unit}</Text>
        )}
      </Flex>
    );
  }

  return (
    <Input
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      bg={inputBg}
      border='1px solid'
      borderColor={inputBorder}
      borderRadius='10px'
      fontSize='sm'
      _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
    />
  );
}

// ── Main component ──────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  episode: ClinicalEpisode;
  editEval?: PhysicalEvaluation;
}

export default function EvaluationFormModal({ isOpen, onClose, episode, editEval }: Props) {
  const toast      = useToast();
  const { data: currentUser } = useCurrentDbUser();
  const createEval = useCreateEvaluation();
  const updateEval = useUpdateEvaluation();
  const isEdit = !!editEval;

  const mutedColor      = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const inputBg         = useColorModeValue('gray.50', 'navy.700');
  const inputBorder     = useColorModeValue('gray.200', 'whiteAlpha.200');
  const accordionBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const headerBg        = useColorModeValue('gray.50', 'navy.700');
  const rowBg           = useColorModeValue('gray.50', 'navy.750');
  const rowBorder       = useColorModeValue('gray.200', 'whiteAlpha.150');
  const sectionBg       = useColorModeValue('gray.50', 'navy.750');

  // ── Step 0 = selector de tipo, Step 1 = formulario
  const [step,            setStep]            = useState<0 | 1>(0);
  const [tipoEvaluacion,  setTipoEvaluacion]  = useState<TipoEvaluacion | null>(null);
  const [templateData,    setTemplateData]    = useState<Record<string, unknown>>({});

  const [fechaEvaluacion, setFechaEvaluacion] = useState(TODAY);
  const [escalaDolor,     setEscalaDolor]     = useState(5);
  const [inspeccion,      setInspeccion]      = useState('');
  const [palpacion,       setPalpacion]       = useState('');
  const [diagnostico,     setDiagnostico]     = useState('');
  const [observaciones,   setObservaciones]   = useState('');
  const [romRows,         setRomRows]         = useState<KVRow[]>([{ key: '', value: '' }]);
  const [fuerzaRows,      setFuerzaRows]      = useState<KVRow[]>([{ key: '', value: '' }]);
  const [pruebaRows,      setPruebaRows]      = useState<PruebaRow[]>([]);
  const [errors,          setErrors]          = useState<Record<string, string>>({});
  const [isSubmitting,    setIsSubmitting]    = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && editEval) {
      setStep(1);
      setTipoEvaluacion(editEval.tipoEvaluacion ?? null);
      setTemplateData((editEval.evaluationData as Record<string, unknown>) ?? {});
      setFechaEvaluacion(editEval.fechaEvaluacion);
      setEscalaDolor(editEval.escalaDolor ?? 5);
      setInspeccion(editEval.inspeccion ?? '');
      setPalpacion(editEval.palpacion ?? '');
      setDiagnostico(editEval.diagnostico ?? '');
      setObservaciones(editEval.observaciones ?? '');
      setRomRows(recordToRows(editEval.rangoMovimiento));
      setFuerzaRows(recordToRows(editEval.fuerzaMuscular));
      setPruebaRows(pruebasToRows(editEval.pruebasEspecificas));
    } else {
      setStep(0);
      setTipoEvaluacion(null);
      setTemplateData({});
      setFechaEvaluacion(TODAY);
      setEscalaDolor(5);
      setInspeccion('');
      setPalpacion('');
      setDiagnostico('');
      setObservaciones('');
      setRomRows([{ key: '', value: '' }]);
      setFuerzaRows([{ key: '', value: '' }]);
      setPruebaRows([]);
    }
    setErrors({});
  }, [isOpen, editEval, isEdit]);

  const setTemplateField = (fieldId: string, value: unknown) =>
    setTemplateData((prev) => ({ ...prev, [fieldId]: value }));

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!fechaEvaluacion)          e.fecha = 'Requerido';
    if (fechaEvaluacion > TODAY)   e.fecha = 'No puede ser fecha futura';
    romRows.forEach((r, i) => {
      if (r.key.trim() && (isNaN(Number(r.value)) || r.value.trim() === ''))
        e[`rom_${i}`] = 'Número requerido';
    });
    fuerzaRows.forEach((r, i) => {
      if (r.key.trim() && (isNaN(Number(r.value)) || r.value.trim() === ''))
        e[`fuerza_${i}`] = 'Número requerido';
      if (r.key.trim() && r.value.trim() && (Number(r.value) < 0 || Number(r.value) > 5))
        e[`fuerza_${i}`] = 'MRC: 0–5';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => {
    const isTrauma = tipoEvaluacion === TipoEvaluacion.TRAUMATOLOGICA;

    const rangoMovimiento = isTrauma
      ? romRows
          .filter((r) => r.key.trim() && r.value.trim() && !isNaN(Number(r.value)))
          .reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.key.trim()]: Number(r.value) }), {})
      : {};

    const fuerzaMuscular = isTrauma
      ? fuerzaRows
          .filter((r) => r.key.trim() && r.value.trim() && !isNaN(Number(r.value)))
          .reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.key.trim()]: Number(r.value) }), {})
      : {};

    const pruebasEspecificas = isTrauma
      ? pruebaRows
          .filter((r) => r.nombre.trim())
          .reduce<Record<string, { resultado: 'positivo' | 'negativo' | 'dudoso'; notas?: string }>>((acc, r) => ({
            ...acc,
            [r.nombre.trim()]: {
              resultado: r.resultado,
              ...(r.notas.trim() ? { notas: r.notas.trim() } : {}),
            },
          }), {})
      : {};

    const nonEmptyTemplateData = Object.keys(templateData).length > 0 ? templateData : undefined;

    return {
      fechaEvaluacion,
      profesionalId: currentUser!.id,
      escalaDolor,
      ...(tipoEvaluacion          ? { tipoEvaluacion }                            : {}),
      ...(nonEmptyTemplateData     ? { evaluationData: nonEmptyTemplateData }      : {}),
      ...(Object.keys(rangoMovimiento).length    ? { rangoMovimiento }             : {}),
      ...(Object.keys(fuerzaMuscular).length     ? { fuerzaMuscular }              : {}),
      ...(Object.keys(pruebasEspecificas).length ? { pruebasEspecificas }          : {}),
      ...(inspeccion.trim()    ? { inspeccion: inspeccion.trim() }                 : {}),
      ...(palpacion.trim()     ? { palpacion: palpacion.trim() }                   : {}),
      ...(diagnostico.trim()   ? { diagnostico: diagnostico.trim() }               : {}),
      ...(observaciones.trim() ? { observaciones: observaciones.trim() }           : {}),
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!currentUser?.id) {
      toast({ title: 'Sin sesión de usuario', status: 'error', duration: 3000, position: 'top-right', isClosable: true });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit && editEval) {
        const { profesionalId, ...updatePayload } = payload;
        await updateEval.mutateAsync({
          patientId: episode.pacienteId,
          episodeId: episode.id,
          evalId: editEval.id,
          payload: updatePayload,
        });
        toast({ title: `Evaluación #${editEval.numeroEvaluacion} actualizada`, status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      } else {
        await createEval.mutateAsync({
          patientId: episode.pacienteId,
          episodeId: episode.id,
          payload,
        });
        toast({ title: 'Evaluación registrada', status: 'success', duration: 2500, isClosable: true, position: 'top-right' });
      }
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        status === 422 ? 'El episodio está cerrado o archivado' :
        status === 403 ? 'Solo el autor o admin puede editar' :
        status === 400 ? (err?.response?.data?.message ?? 'Fecha futura no permitida') :
        err?.response?.data?.message ?? 'Error al guardar';
      toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true, position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updatePrueba = (i: number, field: keyof PruebaRow, val: string) =>
    setPruebaRows((prev) => prev.map((r, j) => j === i ? { ...r, [field]: val } : r));

  const isTrauma = tipoEvaluacion === TipoEvaluacion.TRAUMATOLOGICA;
  const typeConfig = tipoEvaluacion ? EVALUATION_TYPE_CONFIGS[tipoEvaluacion] : null;

  // ── Step 0: type selector
  if (step === 0) {
    return (
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title='Nueva Evaluación — Seleccionar Tipo'
        size='md'
        hideFooter
      >
        <Flex direction='column' gap='10px'>
          <Text fontSize='xs' color={mutedColor} mb='4px'>
            Selecciona el tipo de evaluación para ver el formulario correspondiente.
          </Text>
          {Object.values(TipoEvaluacion).map((tipo) => (
            <TypeCard
              key={tipo}
              tipo={tipo}
              selected={tipoEvaluacion === tipo}
              onSelect={() => { setTipoEvaluacion(tipo); setStep(1); }}
            />
          ))}
        </Flex>
      </CustomModal>
    );
  }

  // ── Step 1: full form
  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEdit
          ? `Editar Evaluación #${editEval?.numeroEvaluacion}`
          : tipoEvaluacion
            ? `Nueva Evaluación — ${TIPO_EVALUACION_META[tipoEvaluacion].label}`
            : 'Nueva Evaluación Física'
      }
      size='xl'
      scrollBehavior='inside'
      primaryBtnLabel={isEdit ? 'Guardar cambios' : 'Registrar evaluación'}
      onPrimaryClick={handleSubmit}
      primaryBtnLoading={isSubmitting}
      secondaryBtnLabel={isEdit ? 'Cancelar' : '← Cambiar tipo'}
      onSecondaryClick={isEdit ? onClose : () => setStep(0)}
    >
      {/* Tipo badge (edit mode) */}
      {isEdit && tipoEvaluacion && (
        <Badge
          colorScheme={TIPO_EVALUACION_META[tipoEvaluacion].color}
          borderRadius='full'
          px='10px'
          py='3px'
          fontSize='xs'
          mb='16px'
          display='block'
          w='fit-content'
        >
          {TIPO_EVALUACION_META[tipoEvaluacion].label}
        </Badge>
      )}

      {/* Fecha */}
      <FormControl isInvalid={!!errors.fecha} mb='20px'>
        <FormLabel ms='2px' fontSize='xs' fontWeight='700' color={mutedColor} mb='4px'>
          Fecha de evaluación *
        </FormLabel>
        <Input
          type='date'
          value={fechaEvaluacion}
          onChange={(e) => { setFechaEvaluacion(e.target.value); setErrors((er) => ({ ...er, fecha: '' })); }}
          max={TODAY}
          bg={inputBg}
          border='1px solid'
          borderColor={errors.fecha ? 'red.400' : inputBorder}
          borderRadius='12px'
          fontSize='sm'
          maxW='200px'
          _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
        />
        {errors.fecha && <FormErrorMessage ms='4px'>{errors.fecha}</FormErrorMessage>}
      </FormControl>

      {/* EVA */}
      <FormControl mb='20px'>
        <FormLabel ms='2px' fontSize='xs' fontWeight='700' color={mutedColor} mb='4px'>
          Escala de Dolor (EVA)
        </FormLabel>
        <Box pt='2'>
          <SimpleSlider
            value={escalaDolor}
            onChange={setEscalaDolor}
            min={0}
            max={10}
            step={1}
            emojis={EVA_EMOJIS}
            label='Nivel de Dolor'
            showValue={true}
            size='md'
          />
        </Box>
      </FormControl>

      <Accordion allowMultiple defaultIndex={[0]} borderColor={accordionBorder}>

        {/* ── Template sections (no-traumatológica) ── */}
        {!isTrauma && typeConfig && typeConfig.sections.map((section) => (
          <AccordionItem key={section.id} border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
            <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
              <Box flex={1} textAlign='left'>
                <Text fontWeight='700' fontSize='sm'>{section.label}</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px='14px' pt='14px' pb='14px'>
              <Flex direction='column' gap='14px'>
                {section.fields.map((field) => (
                  <FormControl key={field.id}>
                    <FormLabel ms='2px' fontSize='xs' fontWeight='700' color={mutedColor} mb='6px'>
                      {field.label}
                      {field.unit && <Text as='span' fontWeight='400' ms='4px'>({field.unit})</Text>}
                    </FormLabel>
                    <TemplateFieldInput
                      field={field}
                      value={templateData[field.id]}
                      onChange={(v) => setTemplateField(field.id, v)}
                    />
                  </FormControl>
                ))}
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        ))}

        {/* ── Tests predefinidos por tipo ── */}
        {typeConfig && typeConfig.tests.length > 0 && (
          <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
            <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
              <Box flex={1} textAlign='left'>
                <SectionLabel icon={MdScience} title='Tests' color='purple.400' />
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px='14px' pt='14px' pb='14px'>
              <Flex direction='column' gap='14px'>
                {typeConfig.tests.map((test) => (
                  <FormControl key={test.id}>
                    <FormLabel ms='2px' fontSize='xs' fontWeight='700' color={mutedColor} mb='6px'>
                      {test.nombre}
                      {test.unit && <Text as='span' fontWeight='400' ms='4px'>({test.unit})</Text>}
                    </FormLabel>
                    {test.tipo === 'boolean' ? (
                      <Flex align='center' gap='10px'>
                        <Switch
                          isChecked={Boolean(templateData[`test_${test.id}`])}
                          onChange={(e) => setTemplateField(`test_${test.id}`, e.target.checked)}
                          colorScheme='brand'
                        />
                        <Text fontSize='sm' color={mutedColor}>{Boolean(templateData[`test_${test.id}`]) ? 'Positivo' : 'Negativo'}</Text>
                      </Flex>
                    ) : test.tipo === 'select' ? (
                      <Select
                        value={String(templateData[`test_${test.id}`] ?? '')}
                        onChange={(e) => setTemplateField(`test_${test.id}`, e.target.value)}
                        bg={inputBg}
                        border='1px solid'
                        borderColor={inputBorder}
                        borderRadius='10px'
                        fontSize='sm'
                        placeholder='Seleccionar...'
                        _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                      >
                        {test.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                      </Select>
                    ) : (
                      <Flex align='center' gap='8px'>
                        <NumberInput
                          value={templateData[`test_${test.id}`] !== undefined ? Number(templateData[`test_${test.id}`]) : ''}
                          onChange={(_, n) => setTemplateField(`test_${test.id}`, isNaN(n) ? '' : n)}
                          min={0}
                          maxW='120px'
                        >
                          <NumberInputField
                            bg={inputBg}
                            border='1px solid'
                            borderColor={inputBorder}
                            borderRadius='10px'
                            fontSize='sm'
                            _focus={{ borderColor: 'brand.500', boxShadow: 'none' }}
                          />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        {test.unit && <Text fontSize='xs' color={mutedColor}>{test.unit}</Text>}
                      </Flex>
                    )}
                    {test.interpretacion && templateData[`test_${test.id}`] !== undefined && (
                      <Text fontSize='10px' color='brand.500' mt='4px'>
                        {/* Simple interpretación — busca rango que match */}
                        {Object.entries(test.interpretacion).find(([range]) => {
                          const val = Number(templateData[`test_${test.id}`]);
                          if (range.startsWith('<'))  return val < Number(range.slice(1));
                          if (range.startsWith('>'))  return val > Number(range.slice(1));
                          if (range.startsWith('>=')) return val >= Number(range.slice(2));
                          const [lo, hi] = range.split('-').map(Number);
                          return val >= lo && val <= hi;
                        })?.[1]}
                      </Text>
                    )}
                  </FormControl>
                ))}
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* ── Traumatológica: ROM + Fuerza + Pruebas específicas ── */}
        {isTrauma && (
          <>
            {/* Inspección / Palpación */}
            <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
              <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
                <Box flex={1} textAlign='left'>
                  <SectionLabel icon={MdScience} title='Inspección y Palpación' color='teal.400' />
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px='14px' pt='14px' pb='4px'>
                <TA label='Inspección' value={inspeccion} onChange={setInspeccion}
                  placeholder='Postura, marcha, actitud antálgica...' rows={3} maxLen={2000} />
                <TA label='Palpación' value={palpacion} onChange={setPalpacion}
                  placeholder='Puntos gatillo, tensión muscular, temperatura...' rows={3} maxLen={2000} />
              </AccordionPanel>
            </AccordionItem>

            {/* ROM */}
            <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
              <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
                <Box flex={1} textAlign='left'>
                  <SectionLabel icon={MdStraighten} title='Rango de Movimiento (ROM)' color='blue.400' />
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px='14px' pt='14px' pb='4px'>
                <Text fontSize='xs' color={mutedColor} mb='10px'>
                  Clave: segmento + movimiento + lado. Ej: <Text as='span' fontFamily='mono'>hombroFlexionD</Text>
                </Text>
                <KVSection
                  label='Mediciones ROM' unit='°'
                  rows={romRows} setRows={setRomRows}
                  prefix='rom' valuePlaceholder='90'
                  errors={errors}
                />
              </AccordionPanel>
            </AccordionItem>

            {/* Fuerza Muscular */}
            <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
              <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
                <Box flex={1} textAlign='left'>
                  <SectionLabel icon={MdFitnessCenter} title='Fuerza Muscular (Escala MRC)' color='purple.400' />
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px='14px' pt='14px' pb='4px'>
                <Text fontSize='xs' color={mutedColor} mb='10px'>
                  Escala MRC 0–5. Clave: músculo + lado. Ej: <Text as='span' fontFamily='mono'>deltoidesD</Text>
                </Text>
                <KVSection
                  label='Músculo' unit='/ 5'
                  rows={fuerzaRows} setRows={setFuerzaRows}
                  prefix='fuerza' valuePlaceholder='4' valueMax={5}
                  errors={errors}
                />
              </AccordionPanel>
            </AccordionItem>

            {/* Pruebas Específicas */}
            <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
              <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
                <Box flex={1} textAlign='left'>
                  <SectionLabel icon={MdScience} title='Pruebas Específicas' color='orange.400' />
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px='14px' pt='14px' pb='4px'>
                <Flex direction='column' gap='8px' mb='8px'>
                  {pruebaRows.map((row, i) => (
                    <Flex key={i} gap='8px' align='flex-start' bg={rowBg} p='10px' borderRadius='10px' border='1px solid' borderColor={rowBorder}>
                      <Input
                        value={row.nombre}
                        onChange={(e) => updatePrueba(i, 'nombre', e.target.value)}
                        placeholder='Ej: Neer, Hawkins, Lasègue'
                        bg='transparent'
                        border='none'
                        borderBottom='1px solid'
                        borderColor={inputBorder}
                        borderRadius='0'
                        fontSize='sm'
                        fontWeight='600'
                        flex={2}
                        _focus={{ boxShadow: 'none', borderColor: 'brand.500' }}
                      />
                      <Select
                        value={row.resultado}
                        onChange={(e) => updatePrueba(i, 'resultado', e.target.value)}
                        bg={inputBg}
                        border='1px solid'
                        borderColor={inputBorder}
                        borderRadius='10px'
                        fontSize='sm'
                        flex={1}
                        _focus={{ boxShadow: 'none' }}>
                        <option value='positivo'>Positivo</option>
                        <option value='negativo'>Negativo</option>
                        <option value='dudoso'>Dudoso</option>
                      </Select>
                      <IconButton
                        aria-label='Eliminar'
                        icon={<Icon as={MdClose} />}
                        size='sm'
                        variant='ghost'
                        onClick={() => setPruebaRows((prev) => prev.filter((_, j) => j !== i))}
                      />
                      <Input
                        value={row.notas}
                        onChange={(e) => updatePrueba(i, 'notas', e.target.value)}
                        placeholder='Notas opcionales'
                        bg='transparent'
                        border='none'
                        borderBottom='1px solid'
                        borderColor={inputBorder}
                        borderRadius='0'
                        fontSize='xs'
                        flex={2}
                        maxLength={200}
                        _focus={{ boxShadow: 'none', borderColor: 'brand.500' }}
                      />
                    </Flex>
                  ))}
                </Flex>
                <Button
                  size='xs' variant='ghost'
                  leftIcon={<Icon as={MdAdd} />}
                  onClick={() => setPruebaRows((prev) => [...prev, { nombre: '', resultado: 'negativo', notas: '' }])}>
                  Añadir prueba
                </Button>
              </AccordionPanel>
            </AccordionItem>
          </>
        )}

        {/* Diagnóstico y observaciones — todos los tipos */}
        <AccordionItem border='1px solid' borderColor={accordionBorder} borderRadius='12px' mb='8px' overflow='hidden'>
          <AccordionButton px='14px' py='12px' bg={headerBg} _hover={{ bg: headerBg }} _expanded={{ bg: headerBg }}>
            <Box flex={1} textAlign='left'>
              <SectionLabel icon={MdScience} title='Diagnóstico y Observaciones' color='green.400' />
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px='14px' pt='14px' pb='4px'>
            <TA label='Diagnóstico fisioterapéutico' value={diagnostico} onChange={setDiagnostico}
              placeholder='Diagnóstico basado en hallazgos de la evaluación...' rows={2} maxLen={1000} />
            <TA label='Observaciones' value={observaciones} onChange={setObservaciones}
              placeholder='Notas adicionales...' rows={2} maxLen={1000} />
          </AccordionPanel>
        </AccordionItem>

      </Accordion>
    </CustomModal>
  );
}
